/**
 * DataView.tsx — Universal data browser for all ingested OpCentral data
 * Domains: email, chat, people, tasks, documents, files
 * Documents sub-categorized by source family (codebase / SharePoint / external)
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// ── Types ──

type Domain = 'email' | 'chat' | 'people' | 'tasks' | 'documents' | 'files';

interface DomainInfo {
  domain: Domain;
  label: string;
  icon: string;
  count: number;
}

interface DataResult {
  id: string;
  domain: Domain;
  title: string;
  description?: string;
  preview?: string;
  metadata: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

// ── Constants ──

const FAMILY_ICONS: Record<string, string> = {
  codebase: '💻',
  sharepoint: '📋',
  conversations: '💬',
  ember: '🔥',
  external: '📂',
};

const DOMAIN_ICONS: Record<Domain, string> = {
  email: '✉️',
  chat: '💬',
  people: '👤',
  tasks: '✅',
  documents: '📄',
  files: '📁',
};

// ── Helpers ──

function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

// ── Main Component ──

export function DataView() {
  const [domains, setDomains] = useState<DomainInfo[]>([]);
  const [docBreakdown, setDocBreakdown] = useState<Record<string, number>>({});
  const [activeDomain, setActiveDomain] = useState<Domain | 'all'>('all');
  const [activeDocFamily, setActiveDocFamily] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DataResult[]>([]);
  const [selectedItem, setSelectedItem] = useState<DataResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const LIMIT = 50;

  // Load domain counts + doc breakdown on mount
  useEffect(() => {
    fetch('/api/data/domains')
      .then((r) => r.json())
      .then((data: DomainInfo[]) => setDomains(data))
      .catch((e) => setError(e.message));

    fetch('/api/data/documents/breakdown')
      .then((r) => r.json())
      .then((data: Record<string, number>) => setDocBreakdown(data))
      .catch(() => {});
  }, []);

  const loadResults = useCallback(
    async (q: string, domain: Domain | 'all', docFamily: string | null, off: number) => {
      setLoading(true);
      setError(null);
      try {
        let url: string;
        if (q) {
          const params = new URLSearchParams({ q, limit: String(LIMIT), offset: String(off) });
          if (domain !== 'all') params.set('domain', domain);
          url = `/api/data/search?${params}`;
        } else {
          if (domain === 'all') {
            url = `/api/data/recent?limit=${LIMIT}`;
          } else {
            const params = new URLSearchParams({ limit: String(LIMIT), offset: String(off) });
            if (domain === 'documents' && docFamily) params.set('source', docFamily);
            url = `/api/data/browse/${domain}?${params}`;
          }
        }

        const res = await fetch(url);
        const data = await res.json();
        const items: DataResult[] = data.results ?? data;

        if (off === 0) {
          setResults(items);
        } else {
          setResults((prev) => [...prev, ...items]);
        }
        setHasMore(items.length === LIMIT);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Debounced search on query/domain/family change
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setOffset(0);
    setSelectedItem(null);
    searchTimer.current = setTimeout(() => {
      loadResults(query, activeDomain, activeDocFamily, 0);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query, activeDomain, activeDocFamily, loadResults]);

  const handleDomainClick = (domain: Domain | 'all') => {
    setActiveDomain(domain);
    setActiveDocFamily(null); // reset family filter when switching domain
  };

  const handleLoadMore = () => {
    const next = offset + LIMIT;
    setOffset(next);
    loadResults(query, activeDomain, activeDocFamily, next);
  };

  const totalCount = domains.reduce((sum, d) => sum + d.count, 0);

  const allDomainInfo = { domain: 'all' as const, label: 'All', icon: '🔍', count: totalCount };

  return (
    <div className="amp-data-root">
      {/* Sidebar */}
      <div className="amp-data-sidebar">
        <div className="amp-data-sidebar-section">
          {[allDomainInfo, ...domains].map((d) => (
            <div key={d.domain}>
              <div
                className={`amp-data-sidebar-item${
                  activeDomain === d.domain && (d.domain !== 'documents' || !activeDocFamily)
                    ? ' amp-data-sidebar-item--active'
                    : ''
                }`}
                onClick={() => handleDomainClick(d.domain as Domain | 'all')}
              >
                <span>{d.icon}</span>
                <span>{d.label}</span>
                <span className="amp-data-sidebar-count">{fmtCount(d.count)}</span>
              </div>

              {/* Document sub-categories */}
              {d.domain === 'documents' && Object.keys(docBreakdown).length > 0 && (
                <div>
                  {Object.entries(docBreakdown)
                    .sort(([, a], [, b]) => b - a)
                    .map(([family, count]) => (
                      <div
                        key={family}
                        className={`amp-data-sidebar-item amp-data-sidebar-item--indent${
                          activeDomain === 'documents' && activeDocFamily === family
                            ? ' amp-data-sidebar-item--active'
                            : ''
                        }`}
                        onClick={() => {
                          setActiveDomain('documents');
                          setActiveDocFamily(family);
                        }}
                      >
                        <span style={{ fontSize: '0.75rem' }}>{FAMILY_ICONS[family] || '📂'}</span>
                        <span style={{ textTransform: 'capitalize' }}>{family}</span>
                        <span className="amp-data-sidebar-count">{fmtCount(count)}</span>
                      </div>
                    ))}
                  {/* Placeholder for future SharePoint ingestion */}
                  {!docBreakdown['sharepoint'] && (
                    <div className="amp-data-sidebar-item amp-data-sidebar-item--indent amp-data-sidebar-item--disabled">
                      <span style={{ fontSize: '0.75rem' }}>📋</span>
                      <span>SharePoint</span>
                      <span className="amp-data-sidebar-count">—</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="amp-data-main">
        {/* Search bar */}
        <div className="amp-data-topbar">
          <input
            className="amp-data-search"
            type="text"
            placeholder={
              activeDomain === 'all'
                ? 'Search everything…'
                : activeDomain === 'documents' && activeDocFamily
                  ? `Search ${activeDocFamily} documents…`
                  : `Search ${activeDomain}…`
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="amp-data-clear" onClick={() => setQuery('')}>
              ✕
            </button>
          )}
          {/* Active family chip */}
          {activeDomain === 'documents' && activeDocFamily && (
            <button
              className="amp-data-filter-chip amp-data-filter-chip--active"
              onClick={() => setActiveDocFamily(null)}
            >
              {FAMILY_ICONS[activeDocFamily]} {activeDocFamily} ✕
            </button>
          )}
        </div>

        <div className="amp-data-content">
          {/* Results list */}
          <div className="amp-data-results">
            {error && <div className="amp-data-error">{error}</div>}

            {loading && results.length === 0 && (
              <div className="amp-data-empty">
                <span>Loading…</span>
              </div>
            )}

            {!loading && results.length === 0 && !error && (
              <div className="amp-data-empty">
                <span style={{ fontSize: '2rem' }}>
                  {activeDomain === 'all'
                    ? '🔍'
                    : activeDomain === 'documents' && activeDocFamily
                      ? FAMILY_ICONS[activeDocFamily] || '📄'
                      : DOMAIN_ICONS[activeDomain as Domain]}
                </span>
                <span>{query ? 'No results' : 'No data yet'}</span>
                {activeDomain === 'documents' && activeDocFamily === 'sharepoint' && (
                  <span
                    style={{
                      fontSize: '0.75rem',
                      color: '#374151',
                      textAlign: 'center',
                      maxWidth: 200,
                    }}
                  >
                    SharePoint content will appear here once ingestion runs
                  </span>
                )}
              </div>
            )}

            {results.map((item) => (
              <div
                key={`${item.domain}-${item.id}`}
                className={`amp-data-result-card${
                  selectedItem?.id === item.id && selectedItem?.domain === item.domain
                    ? ' amp-data-result-card--selected'
                    : ''
                }`}
                onClick={() => setSelectedItem(item)}
              >
                <div className="amp-data-result-title">{item.title}</div>
                <div className="amp-data-result-meta">
                  <span className="amp-data-domain-badge" data-domain={item.domain}>
                    {item.domain}
                  </span>
                  {item.metadata?.family && item.domain === 'documents' && (
                    <span className="amp-data-family-badge" data-family={item.metadata.family}>
                      {item.metadata.sourceLabel || item.metadata.family}
                    </span>
                  )}
                  {item.description && <span>{item.description}</span>}
                  {item.createdAt && <span>{fmtDate(item.createdAt)}</span>}
                </div>
                {item.preview && <div className="amp-data-result-preview">{item.preview}</div>}
              </div>
            ))}

            {hasMore && (
              <button className="amp-data-load-more" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>

          {/* Detail panel */}
          {selectedItem && (
            <div className="amp-data-detail">
              <div className="amp-data-detail-badges">
                <span className="amp-data-domain-badge" data-domain={selectedItem.domain}>
                  {selectedItem.domain}
                </span>
                {selectedItem.metadata?.family && selectedItem.domain === 'documents' && (
                  <span
                    className="amp-data-family-badge"
                    data-family={selectedItem.metadata.family}
                  >
                    {FAMILY_ICONS[selectedItem.metadata.family]}{' '}
                    {selectedItem.metadata.sourceLabel || selectedItem.metadata.family}
                  </span>
                )}
                {selectedItem.createdAt && (
                  <span className="amp-data-timestamp">{fmtDate(selectedItem.createdAt)}</span>
                )}
              </div>

              <div className="amp-data-detail-title">{selectedItem.title}</div>

              {selectedItem.description && (
                <div className="amp-data-detail-desc">{selectedItem.description}</div>
              )}

              {selectedItem.preview && (
                <div className="amp-data-detail-section">
                  <div className="amp-data-detail-label">Path / Preview</div>
                  <div
                    className="amp-data-detail-value amp-data-detail-mono"
                    style={{ whiteSpace: 'pre-wrap', maxHeight: 180, overflowY: 'auto' }}
                  >
                    {selectedItem.preview}
                  </div>
                </div>
              )}

              <div className="amp-data-detail-section">
                <div className="amp-data-detail-label">Metadata</div>
                {Object.entries(selectedItem.metadata).map(([k, v]) => {
                  if (v === null || v === undefined || v === '') return null;
                  const display = typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v);
                  return (
                    <div key={k} style={{ marginBottom: 5 }}>
                      <div className="amp-data-detail-meta-key">{k}</div>
                      <div
                        className={`amp-data-detail-value${typeof v === 'object' ? ' amp-data-detail-mono' : ''}`}
                        style={{ fontSize: '0.73rem' }}
                      >
                        {display}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedItem.updatedAt && (
                <div className="amp-data-detail-section">
                  <div className="amp-data-detail-label">Last Modified</div>
                  <div className="amp-data-detail-value">{fmtDate(selectedItem.updatedAt)}</div>
                </div>
              )}

              <div className="amp-data-detail-section amp-data-detail-id">
                <div className="amp-data-detail-label">ID</div>
                <div
                  className="amp-data-detail-value amp-data-detail-mono"
                  style={{ fontSize: '0.68rem', wordBreak: 'break-all' }}
                >
                  {selectedItem.id}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DataView;
